import * as ld from 'launchdarkly-js-client-sdk';
import React, { useState, useEffect, useContext, useCallback, Fragment } from 'react';

/**
 * This is the client side IDs for the LaunchDarkly project used in production.
 * They are not secret, and can safely be commited to repo and sent to clients.
 */ var TEST_CLIENT_SIDE_ID = "5fab9aabda54e709eea375db";
var PRODUCTION_CLIENT_SIDE_ID = "5fab9aabda54e709eea375dc";
var CLIENT_SIDE_ID = process.env.NODE_ENV === "development" ? TEST_CLIENT_SIDE_ID : PRODUCTION_CLIENT_SIDE_ID;

var FeatureToggleContext = /*#__PURE__*/ React.createContext(null);
var FeatureToggleContextProvider = function(param) {
    var userEmail = param.userEmail, tenant = param.tenant, clientName = param.clientName, children = param.children;
    var _useState = useState(), client = _useState[0], setClient = _useState[1];
    useEffect(function initializeClient() {
        var userData = {
            key: userEmail,
            email: userEmail,
            custom: {
                client_name: clientName || "",
                tenant: tenant
            }
        };
        var newClient = ld.initialize(CLIENT_SIDE_ID, userData);
        newClient.on("initialized", function() {
            setClient(newClient);
        });
        return function() {
            newClient.close();
        };
    }, [
        userEmail,
        tenant,
        clientName
    ]);
    return /*#__PURE__*/ React.createElement(FeatureToggleContext.Provider, {
        value: {
            client: client
        }
    }, children);
};
function useFeatureToggle(featureKey, defaultValue) {
    if (defaultValue === void 0) defaultValue = false;
    var ctx = useContext(FeatureToggleContext);
    if (ctx === null) throw Error("To use the feature toggle system, make sure your app is wrapped in a FeatureToggleContextProvider");
    if (featureKey === undefined) {
        return defaultValue;
    }
    if (ctx.client) {
        return ctx.client.variation(featureKey, defaultValue);
    }
    return defaultValue;
}
var useGetFeatureToggleValue = function() {
    var ctx = useContext(FeatureToggleContext);
    if (ctx === null) throw Error("To use the feature toggle system, make sure your app is wrapped in a FeatureToggleContextProvider");
    return useCallback(function(featureKey, defaultValue) {
        if (defaultValue === void 0) defaultValue = false;
        if (featureKey === undefined) {
            return defaultValue;
        }
        if (ctx.client) {
            return ctx.client.variation(featureKey, defaultValue);
        }
        return defaultValue;
    }, [
        ctx.client
    ]);
};

var FeatureToggleContainer = function(param) {
    var featureKey = param.featureKey, defaultValue = param.defaultValue, children = param.children, hideChildren = param.hideChildren;
    var isFeatureToggled = useFeatureToggle(featureKey, defaultValue);
    if (hideChildren) {
        return isFeatureToggled ? null : /*#__PURE__*/ React.createElement(Fragment, null, children);
    }
    return isFeatureToggled ? /*#__PURE__*/ React.createElement(Fragment, null, children) : null;
};
var OptionalFeatureToggleContainer = function(param) {
    var featureKey = param.featureKey, children = param.children, defaultValue = param.defaultValue, hideChildren = param.hideChildren;
    return featureKey ? /*#__PURE__*/ React.createElement(FeatureToggleContainer, {
        defaultValue: defaultValue,
        featureKey: featureKey,
        hideChildren: hideChildren
    }, children) : /*#__PURE__*/ React.createElement(Fragment, null, children);
};
/**
 * If `featureKey` is toggled, render `children`.
 * Else, render the provided `Fallback` component.
 */ var FeatureToggleWithFallbackContainer = function(param) {
    var featureKey = param.featureKey, defaultValue = param.defaultValue, children = param.children, hideChildren = param.hideChildren, fallback = param.fallback;
    var isFeatureToggled = useFeatureToggle(featureKey, defaultValue);
    if (hideChildren) {
        return isFeatureToggled ? /*#__PURE__*/ React.createElement(Fragment, null, fallback) : /*#__PURE__*/ React.createElement(Fragment, null, children);
    }
    return isFeatureToggled ? /*#__PURE__*/ React.createElement(Fragment, null, children) : /*#__PURE__*/ React.createElement(Fragment, null, fallback);
};

export { FeatureToggleContainer, FeatureToggleContextProvider, FeatureToggleWithFallbackContainer, OptionalFeatureToggleContainer, useFeatureToggle, useGetFeatureToggleValue };
